import { type ReactNode } from "react";
import { Disclosure } from "@/components/design/Disclosure";

/**
 * The banded ranking list: one collapsible card per band, each carrying its own count, and an
 * explanatory line where a band is empty and its emptiness needs explaining.
 *
 * Extracted from `CandidateRankingPanel` so the company ranking is the SAME list rather than a
 * lookalike (Kevin, 2026-09-09). Both sides already cut the same five bands — A, B, C, REVIEW,
 * UNQUALIFIED — so what differs between them is the row body and the nouns in the copy, and both
 * of those arrive from the caller.
 *
 * What lives here: the wrapper rhythm, the per-band test id, the Disclosure with its count and
 * header action, and the three-way empty state (an explaining hint, or a bare em-rule for a band
 * that needs none). What deliberately does NOT live here: which band a row belongs to, and the
 * order the bands are shown in — the caller passes `groups` already ordered, because a band order
 * is a product decision and not this component's to hold.
 */
export interface RankingBandGroup {
  /** Band key, used for the anchor (`${idPrefix}-${band.toLowerCase()}`). */
  band: string;
  title: ReactNode;
  count: number;
  /** Rendered when `count` is 0 and the emptiness is worth a sentence; else a bare rule shows. */
  emptyHint?: ReactNode;
  /** Typically an expand-all / collapse-all button. Omitted for an empty band. */
  headerAction?: ReactNode;
  /** The rows. Only read when `count > 0`. */
  children?: ReactNode;
}

export function RankingBandGroups({ groups, testIdPrefix, idPrefix }: {
  groups: readonly RankingBandGroup[];
  testIdPrefix?: string;
  /** Kennungs-Präfix je Band; die Kandidatenseite adressiert darüber, die Unternehmensseite über `testIdPrefix`. */
  idPrefix?: string;
}) {
  return (
    <div className="space-y-2">
      {groups.map((group) => (
        // A band is a Disclosure so a full A can be folded away instead of scrolling past it to
        // reach B and C.
        <div
          key={group.band}
          id={idPrefix && `${idPrefix}-${group.band.toLowerCase()}`}
          data-testid={testIdPrefix && `${testIdPrefix}-${group.band.toLowerCase()}`}
        >
          <Disclosure
            title={group.title}
            count={group.count}
            headerAction={group.count > 0 ? group.headerAction : null}
            defaultOpen
          >
            {group.count === 0 ? (
              group.emptyHint != null ? (
                <div className="max-w-2xl py-0.5 text-smycles-meta text-ink-muted">{group.emptyHint}</div>
              ) : (
                <div className="py-0.5 text-smycles-meta text-ink-muted">—</div>
              )
            ) : (
              group.children
            )}
          </Disclosure>
        </div>
      ))}
    </div>
  );
}
